import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Layout } from '@/components/ui/Layout';
import {
  Shield,
  Library,
  BarChart3,
  ClipboardList,
  Brain,
  Wrench,
  HelpCircle,
  Settings,
  Building,
  Users,
  ArrowRight,
  Home
} from 'lucide-react';
import { cn } from '@/lib/utils';

interface NavTileProps {
  icon: typeof Home;
  title: string;
  description: string;
  href: string;
  color: string;
}

function NavTile({ icon: Icon, title, description, href, color }: NavTileProps) {
  return (
    <Link 
      to={href}
      className={cn(
        "block p-6 bg-white dark:bg-gray-800 rounded-lg shadow-sm hover:shadow-md",
        "transform transition-all duration-200 hover:-translate-y-1",
        "border border-gray-100 dark:border-gray-700"
      )}
    >
      <div className="flex items-center justify-between mb-4">
        <div className={cn(
          "p-3 rounded-lg",
          color === 'blue' && "bg-blue-100 dark:bg-blue-900/50",
          color === 'green' && "bg-green-100 dark:bg-green-900/50",
          color === 'purple' && "bg-purple-100 dark:bg-purple-900/50",
          color === 'orange' && "bg-orange-100 dark:bg-orange-900/50",
          color === 'red' && "bg-red-100 dark:bg-red-900/50",
          color === 'indigo' && "bg-indigo-100 dark:bg-indigo-900/50"
        )}>
          <Icon className={cn(
            "h-8 w-8",
            color === 'blue' && "text-blue-600 dark:text-blue-400",
            color === 'green' && "text-green-600 dark:text-green-400",
            color === 'purple' && "text-purple-600 dark:text-purple-400",
            color === 'orange' && "text-orange-600 dark:text-orange-400",
            color === 'red' && "text-red-600 dark:text-red-400",
            color === 'indigo' && "text-indigo-600 dark:text-indigo-400"
          )} />
        </div>
        <ArrowRight className="h-5 w-5 text-gray-400 dark:text-gray-500" />
      </div>
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
        {title}
      </h3>
      <p className="text-sm text-gray-600 dark:text-gray-400">
        {description}
      </p>
    </Link>
  );
}

export function Dashboard() {
  const { user } = useAuth();
  const isAdmin = user?.email?.endsWith('@admin.com');

  return (
    <Layout>
      <div className="max-w-7xl mx-auto">
        <h1 className="text-3xl font-semibold text-gray-900 dark:text-white mb-8">
          Supercharge AI Adoption & Performance
        </h1>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Main Navigation Tiles */}
          <NavTile
            icon={Home}
            title="Dashboard"
            description="View key metrics and insights about your AI implementation"
            href="/"
            color="blue"
          />

          <NavTile
            icon={ClipboardList}
            title="Surveys"
            description="Manage and analyze AI readiness surveys"
            href="/surveys"
            color="green"
          />
          
          <NavTile
            icon={Brain}
            title="AI Strategy"
            description="Develop and refine your AI implementation strategy"
            href="/strategy"
            color="purple"
          />

          <NavTile
            icon={Wrench}
            title="Tools"
            description="Access AI implementation and analysis tools"
            href="/tools"
            color="indigo"
          />

          <NavTile
            icon={Library}
            title="Library"
            description="Access guides, documentation and resources"
            href="/library"
            color="orange"
          />

          <NavTile
            icon={BarChart3}
            title="Reports"
            description="View detailed analytics and performance reports"
            href="/reports"
            color="red"
          />

          {/* Admin-only Tiles */}
          {isAdmin && (
            <>
              <NavTile
                icon={Building}
                title="Organizations"
                description="Manage organization accounts and settings"
                href="/organizations"
                color="blue"
              />

              <NavTile
                icon={Users}
                title="Users"
                description="Manage user accounts and permissions"
                href="/users"
                color="green"
              />
            </>
          )}

          {/* Support and Settings */}
          <NavTile
            icon={HelpCircle}
            title="Support"
            description="Get help and contact our support team"
            href="/support"
            color="purple"
          />

          <NavTile
            icon={Settings}
            title="Settings"
            description="Configure your account and preferences"
            href="/settings"
            color="orange"
          />
        </div>
      </div>
    </Layout>
  );
}